import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getActiveBanners } from "../lib/bannerApi";

// Eligible banners are fetched ONCE per view and shared across every slot (module
// cache), never per slot — and each slot renders a single lazy-loaded <img>, so
// no bulk image download happens. A DETERMINISTIC per-page start offset + per-slot
// counter rotate them fairly, so Discover/Search/Calendar each START on a different
// advert (no randomness) while overall rotation stays fair. Falls back to the
// RoadRnr house ad when none are eligible. AdSense/GA4/Consent Mode untouched.
let _slotCounter = 0;

// Stable string hash → deterministic per-page starting offset (no randomness,
// works with any number of active campaigns using the existing banner list).
// Explicit ordinals for pages that must sit adjacently in the pool: Saved starts
// exactly ONE position after Calendar (calendar=N, saved=N+1) regardless of banner
// count. Other pages (Discover/Search) keep their hash-based offset, unchanged.
const PAGE_ORDINAL = { calendar: 0, saved: 1 };
function pageOffset(page) {
  const key = String(page || "default");
  if (key in PAGE_ORDINAL) return PAGE_ORDINAL[key];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return h;
}

function loadBanners() { return getActiveBanners().catch(() => []); }

const HouseAd = ({ className }) => (
  <Link
    to="/advertise"
    data-testid="ad-slot"
    aria-label="Advertise on RoadRnr"
    className={`col-span-2 flex aspect-[3/1] w-full flex-col justify-center overflow-hidden rounded-3xl border border-[#ff2a00]/25 bg-[#0e0e0e] px-5 transition-transform duration-200 active:scale-[0.99] ${className}`}
  >
    <span className="block text-[9px] font-semibold uppercase tracking-[0.22em] text-white/40">
      Sponsored
    </span>
    <span className="font-display mt-1 block text-lg font-extrabold leading-tight tracking-tight text-white">
      Your brand could be here
    </span>
    <span className="mt-1 inline-flex items-center gap-1 text-sm font-bold text-[#ff2a00]">
      Advertise on RoadRnr →
    </span>
  </Link>
);

export const AdSlot = ({ className = "", page = "default" }) => {
  const [banner, setBanner] = useState(undefined); // undefined=loading, null=house

  useEffect(() => {
    const slotIndex = _slotCounter++;
    let alive = true;
    let generation = 0;
    const refresh = () => {
      const current = ++generation;
      setBanner(null);
      loadBanners().then((list) => {
        if (!alive || current !== generation) return;
        setBanner(list.length ? list[(pageOffset(page) + slotIndex) % list.length] : null);
      });
    };
    const visible = () => { if (!document.hidden) refresh(); };
    refresh();
    window.addEventListener("roadrnr-device-location", refresh);
    document.addEventListener("visibilitychange", visible);
    const timer = setInterval(visible, 60000);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener("roadrnr-device-location", refresh);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [page]);

  if (banner === undefined || banner === null) return <HouseAd className={className} />;

  return (
    <div className={`col-span-2 ${className}`}>
      <span className="mb-1 block text-left text-[9px] font-semibold uppercase tracking-[0.22em] text-white/40">
        Sponsored
      </span>
      <a
        href={banner.destination_url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        data-testid="ad-slot"
        className="relative block aspect-[3/1] w-full overflow-hidden rounded-3xl border border-white/10 bg-[#0e0e0e] transition-transform duration-200 active:scale-[0.99]"
      >
        <img
          src={banner.display_url}
          alt="Sponsored"
          loading="lazy"
          className="h-full w-full object-contain"
        />
      </a>
    </div>
  );
};
